import { type FC, type ReactNode, useState, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import {
    BuyerIncentivesBanner,
    type BuyerIncentivesBannerVariant,
} from 'dibs-buyer-incentives/exports/BuyerIncentivesBanner.ts';
import { PdpSharedDibsPromiseModalLazy } from '../../PdpSharedModals/PdpSharedDibsPromiseModal/PdpSharedDibsPromiseModalLazy.tsx';
import { PDP_TRUST_MESSAGING_TEST_NAME } from '../../../constants/abTestsConstants.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type PdpSharedSidebarBuyerIncentives_user$key } from './__generated__/PdpSharedSidebarBuyerIncentives_user.graphql.ts';

const getTrustMessagingVariant = (): BuyerIncentivesBannerVariant => {
    if (typeof window === 'undefined') {
        return 'control';
    }
    const variant = getAbTestV2(PDP_TRUST_MESSAGING_TEST_NAME)?.variant;
    return variant === 'variant' || variant === 'variant-b' ? variant : 'control';
};

const userFragment = graphql`
    fragment PdpSharedSidebarBuyerIncentives_user on User {
        isVerifiedTrade
        ...PdpSharedDibsPromiseModalLazy_user
    }
`;

const consumerTitle = (
    <FormattedMessage
        id="PdpSharedSidebarBuyerIncentives.trustMessaging.consumerTitle"
        defaultMessage="1stDibs Purchase Protection"
    />
);

const consumerDescription = (
    <FormattedMessage
        id="PdpSharedSidebarBuyerIncentives.trustMessaging.consumerDescription"
        defaultMessage="If your item is inauthentic, not as described, or damaged, you get your money back."
    />
);

const tradeTitle = (
    <FormattedMessage
        id="PdpSharedSidebarBuyerIncentives.trustMessaging.tradeTitle"
        defaultMessage="Trade 1st Purchase Protection"
    />
);

const tradeDescription = (
    <FormattedMessage
        id="PdpSharedSidebarBuyerIncentives.trustMessaging.tradeDescription"
        defaultMessage="Authenticity Guarantee, Extended Money Back Guarantee, and a 14-day return window."
    />
);

const PdpSharedSidebarBuyerIncentives: FC<{
    user?: PdpSharedSidebarBuyerIncentives_user$key | null | undefined;
    isPdpSiderailSellerReviewCombinedVariant?: boolean;
}> = ({ user: userRef, isPdpSiderailSellerReviewCombinedVariant = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useServerVarsContext();
    const trustMessagingVariant = getTrustMessagingVariant();

    useEffect(() => {
        trackAbTestV2Variant(PDP_TRUST_MESSAGING_TEST_NAME);
    }, []);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: interactionTypeConstants.INTERACTION_TYPE_EBI_DETAILS,
            step_interaction_name:
                stepInteractionConstants.STEP_INTERACTION_EBI_SIDERAIL_DETAILS_CLICK,
        });
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const getLink = (chunk: ReactNode): ReactNode => {
        return (
            <Link dataTn="dibs-promise-link" href="#" onClick={handleOpenModal}>
                {chunk}
            </Link>
        );
    };

    const isTradeUser = !!user?.isVerifiedTrade;
    const useCustomCopy = trustMessagingVariant !== 'control';

    let titleOverride;
    let descriptionOverride;
    if (useCustomCopy) {
        titleOverride = isTradeUser ? tradeTitle : consumerTitle;
        descriptionOverride = isTradeUser ? tradeDescription : consumerDescription;
    }

    return (
        <>
            <BuyerIncentivesBanner
                linkPosition={isPdpSiderailSellerReviewCombinedVariant ? 'top' : 'bottom'}
                getLink={getLink}
                variant={trustMessagingVariant}
                titleOverride={titleOverride}
                descriptionOverride={descriptionOverride}
                isMobile={!!isMobile}
            />
            <PdpSharedDibsPromiseModalLazy
                user={user}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </>
    );
};

export default PdpSharedSidebarBuyerIncentives;
